import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';

import { ReactComponent as BarChartIcon } from '../../../../assets/icons/chart-icon.svg';
import { ReactComponent as PieChartIcon } from '../../../../assets/icons/pie-chart-icon.svg';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import ToggleWithIcons from '../../../../shared-components/toggle-with-icons/ToggleWithIcons';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import stationManagementApi from '../../../../utils/api/station-management';

interface ChartDataProperties {
  label: string;
  maintenanceAlert: number;
  transactionAlert: number;
  energieConsumptionAlert: number;
  performanceAlert: number;
  [key: string]: string | number;
}

/**
 * Alerts chart component
 * @returns {any} The rendered component
 */
function AlertsChart() {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();

  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [dataToggle, setDataToggle] = useState([
    {
      svgIcon: <PieChartIcon />,
      key: 'chart',
      isActive: true,
      label: '',
    },
    {
      svgIcon: <BarChartIcon />,
      key: 'calender',
      isActive: false,
      label: '',
    },
  ]);

  const chartTimePeriod: {
    label: string;
    value: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months';
  }[] = useMemo(
    () => [
      {
        label: t('last24hours'),
        value: 'last24hours',
      },
      {
        label: t('last7days'),
        value: 'last7days',
      },
      {
        label: t('lastMonth'),
        value: 'lastMonth',
      },
      {
        label: t('last12months'),
        value: 'last12months',
      },
    ],
    [t, i18n.language],
  );

  /**
   * Fetches the power consumed report data for the specified time range and updates the chart data.
   * @param {string} range - The time period for which to fetch the report ('last24hours', 'last7days', 'lastMonth', or 'last12months').
   */
  const fetchToggleData = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    if (!selectedChargingPoint) return;
    try {
      const response = await stationManagementApi.fetchAlertsChart({
        timePeriod: range,
        reportType: 'performance',
        chargingStation: selectedChargingPoint?.id,
      });

      if (response.status === 200) {
        let transformedData: ChartDataProperties[];
        const ok = response.data;
        switch (range) {
          case 'last12months':
            transformedData = fillMissingMonthsGeneric(ok, i18n.language).map((item) => ({
              label: item.label,
              maintenanceAlert: (item as any).maintenanceAlert || 0,
              transactionAlert: (item as any).transactionAlert || 0,
              energieConsumptionAlert: (item as any).energieConsumptionAlert || 0,
              performanceAlert: (item as any).performanceAlert || 0,
            }));
            break;
          case 'lastMonth':
            transformedData = fillMissingLastNDaysGeneric(response.data, 30, i18n.language).map(
              (item) => ({
                label: item.label,
                maintenanceAlert: (item as any).maintenanceAlert || 0,
                transactionAlert: (item as any).transactionAlert || 0,
                energieConsumptionAlert: (item as any).energieConsumptionAlert || 0,
                performanceAlert: (item as any).performanceAlert || 0,
              }),
            );
            break;
          case 'last7days':
            transformedData = fillMissingLastNDaysGeneric(response.data, 7, i18n.language).map(
              (item) => ({
                label: item.label,
                maintenanceAlert: (item as any).maintenanceAlert || 0,
                transactionAlert: (item as any).transactionAlert || 0,
                energieConsumptionAlert: (item as any).energieConsumptionAlert || 0,
                performanceAlert: (item as any).performanceAlert || 0,
              }),
            );
            break;
          case 'last24hours':
            transformedData = fillMissingLast24HrsGeneric(response.data).map((item) => ({
              label: item.label,
              maintenanceAlert: (item as any).maintenanceAlert || 0,
              transactionAlert: (item as any).transactionAlert || 0,
              energieConsumptionAlert: (item as any).energieConsumptionAlert || 0,
              performanceAlert: (item as any).performanceAlert || 0,
            }));
            break;
          default:
            transformedData = [];
        }
        setBarGraphData(transformedData);
        setIsLoading(false);
      }
    } catch (error: any) {
      setIsLoading(false);
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Function to handle content type
   * @param {string} key The key to toggle
   */
  const HandleContentType = (key: string) => {
    setDataToggle((previous) => previous.map((tab) => ({ ...tab, isActive: tab.key === key })));
  };

  /**
   * CustomTooltip displays a tooltip with freeSessionCount and paidSessionCount booking hours.
   * @param {TooltipProps<number, string>} props - TooltipProps from recharts containing active state and payload.
   * @returns {any} The tooltip element
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length) {
      const item = payload[0];

      return (
        <div className="connector-status-custom-tooltip">
          <p
            className="label"
            style={{ color: item.color || item.payload?.fill || item.payload?.color }}>
            {`${t(item.name as string)} : ${item.value}`}
          </p>
        </div>
      );
    }
    return null;
  }

  useEffect(() => {
    fetchToggleData('last24hours');
  }, []);
  const pieData = useMemo(() => {
    return [
      {
        name: t('maintenanceAlert'),
        value: barGraphData.reduce((sum, d) => sum + (d.maintenanceAlert || 0), 0),
        color: '#4ABDFB',
      },
      {
        name: t('transactionAlert'),
        value: barGraphData.reduce((sum, d) => sum + (d.transactionAlert || 0), 0),
        color: '#FB497C',
      },
      {
        name: t('energyConsumption'),
        value: barGraphData.reduce((sum, d) => sum + (d.energieConsumptionAlert || 0), 0),
        color: '#FF7714',
      },
      {
        name: t('performanceAlert'),
        value: barGraphData.reduce((sum, d) => sum + (d.performanceAlert || 0), 0),
        color: '#41DCE5',
      },
    ];
  }, [barGraphData, i18n.language]);

  return (
    <div className="alerts-chart-wrap" dir={i18n.dir()}>
      <p className="alerts-chart-title">{t('alerts')}</p>
      <div className="alerts-chart-menus">
        <div className="alerts-chart-toggle">
          <CustomToggleButton
            labels={chartTimePeriod.map((duration) => duration.label)}
            onToggle={(activeIndex) => {
              fetchToggleData(chartTimePeriod[activeIndex]?.value || 'last12months');
              setIsLoading(true);
            }}
            defaultActiveIndex={0}
            type="chart"
          />
          <div className="alerts-data-toggle">
            <ToggleWithIcons
              leftMenu={dataToggle[0]}
              rightMenu={dataToggle[1]}
              handleClickToggle={HandleContentType}
            />
          </div>
        </div>
        <div className="alerts-charts-container">
          {dataToggle[0].isActive ? (
            <div>
              {isLoading ? (
                <div className="loader">
                  <BatteryLoader />
                </div>
              ) : (
                <div className="pie-chart-wrap">
                  <div className="pie-chart-container">
                    <PieChart
                      width={250}
                      height={250}
                      margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                      <Pie
                        data={pieData}
                        cx={125}
                        cy={125}
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        isAnimationActive={false}>
                        {pieData.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        content={<CustomTooltip />}
                        wrapperStyle={{ zIndex: 1000 }} // Ensure tooltip appears above other elements
                      />
                    </PieChart>
                    <div className="total-sessions-wrap">
                      <p>{t('totalSessions')}</p>
                      <span>
                        {barGraphData.reduce(
                          (sum, d) =>
                            sum +
                            (d.maintenanceAlert || 0) +
                            (d.transactionAlert || 0) +
                            (d.energieConsumptionAlert || 0) +
                            (d.performanceAlert || 0),
                          0,
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="pie-chart-heading">
                    <div className="pie-chart-label">
                      {[
                        {
                          name: t('maintenanceAlert'),
                          value: barGraphData.reduce(
                            (sum, d) => sum + (d.maintenanceAlert || 0),
                            0,
                          ),
                          color: '#4ABDFB',
                        },
                        {
                          name: t('transactionAlert'),
                          value: barGraphData.reduce(
                            (sum, d) => sum + (d.transactionAlert || 0),
                            0,
                          ),
                          color: '#FB497C',
                        },
                        {
                          name: t('energyConsumption'),
                          value: barGraphData.reduce(
                            (sum, d) => sum + (d.energieConsumptionAlert || 0),
                            0,
                          ),
                          color: '#FF7714',
                        },
                        {
                          name: t('performanceAlert'),
                          value: barGraphData.reduce(
                            (sum, d) => sum + (d.performanceAlert || 0),
                            0,
                          ),
                          color: '#41DCE5',
                        },
                      ].map((eachData) => (
                        <div key={eachData.name} className="each-label">
                          <div
                            className="each-label-color"
                            style={{ '--label-color': eachData.color } as React.CSSProperties}
                          />
                          <p>{eachData.name}</p>
                          <div className="each-label-value">
                            <span>{eachData.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bar-chart-wrap">
              {isLoading ? (
                <div className="bar-chart-loader">
                  <BatteryLoader />
                </div>
              ) : (
                <div className="bar-chart-container">
                  <p className="bar-chart-title">{t('frequency')}</p>

                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={barGraphData} barCategoryGap="20%">
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        fontSize={11}
                        stroke="#535353"
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        interval={barGraphData.length > 12 ? 'preserveStartEnd' : 0}
                      />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />

                      <Tooltip />

                      <Bar
                        name={t('maintenanceAlert')}
                        dataKey="maintenanceAlert"
                        fill="#4ABDFB"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        name={t('transactionAlert')}
                        dataKey="transactionAlert"
                        fill="#FB497C"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        name={t('energyConsumption')}
                        dataKey="energieConsumptionAlert"
                        fill="#FF7714"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        name={t('performanceAlert')}
                        dataKey="performanceAlert"
                        fill="#41DCE5"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AlertsChart;
